import * as React from "react";

type CarouselEdges = {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollByCard: (direction: 1 | -1) => void;
  activeIndex: number;
  count: number;
  scrollToIndex: (i: number) => void;
};

function cardStep(el: HTMLElement): number {
  const first = el.querySelector(":scope > *");
  if (!(first instanceof HTMLElement)) return 0;
  const styles = window.getComputedStyle(el);
  const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
  return first.getBoundingClientRect().width + gap;
}

export function useCarouselEdges(ref: React.RefObject<HTMLElement>): CarouselEdges {
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollLeft < max - 1);
      const step = cardStep(el);
      setActiveIndex(step > 0 ? Math.round(el.scrollLeft / step) : 0);
      setCount(el.children.length);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [ref]);

  const scrollByCard = React.useCallback(
    (direction: 1 | -1) => {
      const el = ref.current;
      if (!el) return;
      const step = cardStep(el);
      el.scrollBy({ left: direction * step, behavior: "smooth" });
    },
    [ref],
  );

  const scrollToIndex = React.useCallback(
    (i: number) => {
      const el = ref.current;
      if (!el) return;
      const step = cardStep(el);
      el.scrollTo({ left: i * step, behavior: "smooth" });
    },
    [ref],
  );

  return { canScrollLeft, canScrollRight, scrollByCard, activeIndex, count, scrollToIndex };
}
